import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Modal, EmptyState, LoadingSkeleton, FilterBar, FilterSelect } from '../ui';
import { PlusIcon, PencilIcon, TrashIcon, UserGroupIcon, TrophyIcon } from '@heroicons/react/24/outline';
import { hasAnyRole } from '../../utils/roles';
import { safeErrorMessage } from '../../utils/apiError';
import useApi from '../portal/useApi';
import { formatDate, termLabel } from '../portal/format';
import StudentPicker from '../accounts/StudentPicker';
import StudentField from './StudentField';
import portal from '../portal/Portal.module.css';
import styles from './Houses.module.css';

// Mirrors backend/houses.py's MANAGE_ROLES / RECORD_ROLES
const MANAGE_ROLES = ['administrator', 'principal'];
const RECORD_ROLES = ['administrator', 'principal', 'head_of_academics'];
const NETWORK_ERROR = 'Network error. Please check your connection and try again.';
const today = () => new Date().toISOString().slice(0, 10);

async function send(token, method, path, body) {
  return fetch(`/api/v1/houses${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
}

function HouseName({ house }) {
  return (
    <span className={styles.houseName}>
      <span className={styles.swatch} style={house.color ? { background: house.color } : undefined} />
      {house.name}
    </span>
  );
}

function Points({ value }) {
  return <span className={value < 0 ? styles.minus : styles.plus}>{value > 0 ? `+${value}` : value}</span>;
}

function TermSelect({ terms, value, onChange }) {
  return (
    <FilterSelect
      label="Term"
      value={value}
      onChange={onChange}
      options={terms.map((term) => ({ value: String(term.id), label: termLabel(term) }))}
      allLabel="All time"
    />
  );
}

// ---------------------------------------------------------------- Standings

function StandingsTab({ token, terms, termId, setTermId }) {
  const standings = useApi(token, `/api/v1/houses/standings${termId ? `?term_id=${termId}` : ''}`);
  const rows = standings.data || [];

  return (
    <div>
      <FilterBar>
        <TermSelect terms={terms} value={termId} onChange={setTermId} />
      </FilterBar>
      {standings.error && <div className="alert alert-error">{standings.error}</div>}
      {standings.loading ? (
        <LoadingSkeleton type="card" rows={3} />
      ) : rows.length === 0 ? (
        <Card>
          <EmptyState title="No houses yet" message="Houses appear here once the school has set them up." icon={<TrophyIcon />} />
        </Card>
      ) : (
        <div className={portal.grid}>
          {rows.map((house) => (
            <div key={house.id} className={`${portal.tile} ${styles.standing}`} style={house.color ? { borderTopColor: house.color } : undefined}>
              <div className={portal.tileHead}>
                <div className={portal.tileName}>{house.name}</div>
                <span className={styles.rank}>#{house.rank}</span>
              </div>
              <div>
                <div className={styles.points}>{house.points}</div>
                <div className={styles.pointsLabel}>points from {house.score_count} {house.score_count === 1 ? 'score' : 'scores'}</div>
              </div>
              <div className={portal.tileMeta}>
                {house.member_count} {house.member_count === 1 ? 'student' : 'students'}
                {house.motto ? ` · ${house.motto}` : ''}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------------- Scores

const EMPTY_SCORE = { student_id: '', activity: '', points: '', date: '', notes: '' };

function ScoreModal({ token, score, students, onClose, onSaved }) {
  const isEdit = Boolean(score?.id);
  const [form, setForm] = useState(EMPTY_SCORE);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!score) return;
    setError('');
    setForm(isEdit
      ? { student_id: String(score.student_id), activity: score.activity, points: String(score.points), date: score.date, notes: score.notes || '' }
      : { ...EMPTY_SCORE, date: today() });
  }, [score, isEdit]);

  const set = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const body = { activity: form.activity, points: Number(form.points), date: form.date, notes: form.notes };
      const response = isEdit
        ? await send(token, 'PUT', `/scores/${score.id}`, body)
        : await send(token, 'POST', '/scores', { ...body, student_id: Number(form.student_id) });
      if (response.ok) {
        onSaved();
      } else {
        setError(await safeErrorMessage(response, 'Failed to save score'));
      }
    } catch {
      setError(NETWORK_ERROR);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={Boolean(score)} onClose={onClose} title={isEdit ? 'Edit score' : 'Record a house score'} width="520px">
      <form onSubmit={handleSubmit}>
        {error && <div className="alert alert-error">{error}</div>}
        {isEdit ? (
          <p className={styles.hint}>{score.student_name} · {score.house_name}</p>
        ) : (
          <>
            <StudentField
              id="score-student"
              students={students.filter((student) => student.house_id)}
              value={form.student_id}
              onChange={(value) => setForm((prev) => ({ ...prev, student_id: value }))}
              describe={(student) => ` · ${student.house}`}
            />
            <p className={styles.hint}>Only students who are in a house are listed. The points go to the house they are in now.</p>
          </>
        )}
        <div className="form-group">
          <label htmlFor="score-activity">Activity</label>
          <input id="score-activity" type="text" value={form.activity} onChange={set('activity')} required maxLength={150} placeholder="e.g. Athletics 100m, Quiz night" />
        </div>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="score-points">Points</label>
            <input id="score-points" type="number" step="1" min="-1000" max="1000" value={form.points} onChange={set('points')} required />
            <small>Use a negative number to deduct points.</small>
          </div>
          <div className="form-group">
            <label htmlFor="score-date">Date</label>
            <input id="score-date" type="date" value={form.date} onChange={set('date')} required />
          </div>
        </div>
        <div className="form-group">
          <label htmlFor="score-notes">Notes (optional)</label>
          <textarea id="score-notes" rows={2} value={form.notes} onChange={set('notes')} />
        </div>
        <div className={styles.modalActions}>
          <Button variant="primary" type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save score'}</Button>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
        </div>
      </form>
    </Modal>
  );
}

function ScoresTab({ token, canRecord, terms, termId, setTermId, houses, students }) {
  const [houseId, setHouseId] = useState('');
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [error, setError] = useState('');

  const params = new URLSearchParams();
  if (houseId) params.set('house_id', houseId);
  if (termId) params.set('term_id', termId);
  const scores = useApi(token, `/api/v1/houses/scores?${params}`);
  const rows = scores.data || [];

  const handleDelete = async () => {
    setError('');
    try {
      const response = await send(token, 'DELETE', `/scores/${deleting.id}`);
      if (response.ok) scores.reload();
      else setError(await safeErrorMessage(response, 'Failed to delete score'));
    } catch {
      setError(NETWORK_ERROR);
    } finally {
      setDeleting(null);
    }
  };

  const columns = [
    { header: 'Date', render: (row) => formatDate(row.date) },
    { header: 'Student', render: (row) => <strong>{row.student_name}</strong> },
    { header: 'House', render: (row) => <HouseName house={{ name: row.house_name, color: row.house_color }} /> },
    { header: 'Activity', render: (row) => (<div><div className={styles.entryTitle}>{row.activity}</div>{row.notes && <div className={styles.entryDescription}>{row.notes}</div>}</div>) },
    { header: 'Points', render: (row) => <Points value={row.points} /> },
    { header: 'Recorded by', render: (row) => row.recorded_by_name || '—' },
  ];
  if (canRecord) {
    columns.push({
      header: '',
      render: (row) => (
        <div className={styles.rowActions}>
          <Button variant="ghost" size="sm" onClick={() => setEditing(row)} aria-label={`Edit score for ${row.student_name}`}><PencilIcon style={{ width: 14, height: 14 }} /></Button>
          <Button variant="ghost" size="sm" onClick={() => setDeleting(row)} aria-label={`Delete score for ${row.student_name}`}><TrashIcon style={{ width: 14, height: 14, color: 'var(--color-danger)' }} /></Button>
        </div>
      ),
    });
  }

  return (
    <div>
      <FilterBar onClear={houseId || termId ?() => { setHouseId(''); setTermId(''); } : undefined}>
        <FilterSelect label="House" value={houseId} onChange={setHouseId} options={houses.map((house) => ({ value: String(house.id), label: house.name }))} allLabel="All houses" />
        <TermSelect terms={terms} value={termId} onChange={setTermId} />
        {canRecord && (
          <Button variant="primary" size="sm" onClick={() => setEditing({})}>
            <PlusIcon style={{ width: 16, height: 16 }} />
            Record score
          </Button>
        )}
      </FilterBar>

      {(error || scores.error) && <div className="alert alert-error">{error || scores.error}</div>}

      <Card>
        {scores.loading ? <LoadingSkeleton type="text" rows={6} /> : (
          <Table columns={columns} data={rows} emptyMessage="No house scores recorded for this selection yet." />
        )}
      </Card>

      <ScoreModal token={token} score={editing} students={students} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); scores.reload(); }} />

      <Modal isOpen={Boolean(deleting)} onClose={() => setDeleting(null)} title="Delete score">
        {deleting && (
          <div>
            <p className={styles.hint}>
              Delete <strong>{deleting.activity}</strong> ({deleting.points > 0 ? '+' : ''}{deleting.points}) for {deleting.student_name}? {deleting.house_name}'s total changes straight away.
            </p>
            <div className={styles.modalActions}>
              <Button variant="danger" onClick={handleDelete}>Delete score</Button>
              <Button variant="secondary" onClick={() => setDeleting(null)}>Keep it</Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

// ------------------------------------------------------------------- Manage

function HouseModal({ token, house, onClose, onSaved }) {
  const isEdit = Boolean(house?.id);
  const [form, setForm] = useState({ name: '', motto: '', color: '', useColor: false });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!house) return;
    setError('');
    setForm({ name: house.name || '', motto: house.motto || '', color: house.color || '#1E40AF', useColor: Boolean(house.color) });
  }, [house]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const body = { name: form.name, motto: form.motto, color: form.useColor ? form.color : null };
      const response = isEdit ? await send(token, 'PUT', `/${house.id}`, body) : await send(token, 'POST', '', body);
      if (response.ok) onSaved();
      else setError(await safeErrorMessage(response, 'Failed to save house'));
    } catch {
      setError(NETWORK_ERROR);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={Boolean(house)} onClose={onClose} title={isEdit ? `Edit ${house.name}` : 'Add a house'}>
      <form onSubmit={handleSubmit}>
        {error && <div className="alert alert-error">{error}</div>}
        <div className="form-group">
          <label htmlFor="house-name">Name</label>
          <input id="house-name" type="text" value={form.name} onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))} required maxLength={50} />
        </div>
        <div className="form-group">
          <label htmlFor="house-motto">Motto (optional)</label>
          <input id="house-motto" type="text" value={form.motto} onChange={(e) => setForm((prev) => ({ ...prev, motto: e.target.value }))} maxLength={200} />
        </div>
        <div className={styles.colorRow}>
          <label className={styles.choice}>
            <input type="checkbox" checked={form.useColor} onChange={(e) => setForm((prev) => ({ ...prev, useColor: e.target.checked }))} />
            House colour
          </label>
          {form.useColor && (
            <input type="color" className={styles.colorInput} value={form.color} onChange={(e) => setForm((prev) => ({ ...prev, color: e.target.value }))} aria-label="House colour" />
          )}
        </div>
        <div className={styles.modalActions}>
          <Button variant="primary" type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save house'}</Button>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
        </div>
      </form>
    </Modal>
  );
}

// Tick/untick who is in a house. Ticking a student who is in another house
// moves them; unticking leaves them with no house.
function MembersModal({ token, house, students, onClose, onSaved }) {
  const [selectedIds, setSelectedIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!house) return;
    setError('');
    setSelectedIds(students.filter((student) => student.house_id === house.id).map((student) => student.id));
  }, [house, students]);

  const handleSave = async () => {
    const current = students.filter((student) => student.house_id === house.id).map((student) => student.id);
    const added = selectedIds.filter((id) => !current.includes(id));
    const removed = current.filter((id) => !selectedIds.includes(id));
    setSaving(true);
    setError('');
    try {
      for (const [ids, houseId] of [[added, house.id], [removed, null]]) {
        if (ids.length === 0) continue;
        const response = await send(token, 'PUT', '/assign', { student_ids: ids, house_id: houseId });
        if (!response.ok) {
          setError(await safeErrorMessage(response, 'Failed to update members'));
          return;
        }
      }
      onSaved();
    } catch {
      setError(NETWORK_ERROR);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={Boolean(house)} onClose={onClose} title={house ? `Members — ${house.name}` : ''} width="520px">
      {house && (
        <div>
          {error && <div className="alert alert-error">{error}</div>}
          <p className={styles.hint}>
            Tick the students who belong to {house.name}. Ticking a student from another house moves them here; unticking leaves them with no house. Points already awarded stay with the house they were earned for.
          </p>
          <StudentPicker students={students} selectedIds={selectedIds} onChange={setSelectedIds} />
          <div className={styles.modalActions}>
            <Button variant="primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save members'}</Button>
            <Button variant="secondary" onClick={onClose}>Cancel</Button>
          </div>
        </div>
      )}
    </Modal>
  );
}

function ManageTab({ token, canManage, houses, students, onChanged }) {
  const [editing, setEditing] = useState(null);
  const [members, setMembers] = useState(null);
  const [removing, setRemoving] = useState(null);
  const [error, setError] = useState('');

  const handleRemove = async () => {
    setError('');
    try {
      const response = await send(token, 'DELETE', `/${removing.id}`);
      if (response.ok) onChanged();
      else setError(await safeErrorMessage(response, 'Failed to remove house'));
    } catch {
      setError(NETWORK_ERROR);
    } finally {
      setRemoving(null);
    }
  };

  const unassigned = students.filter((student) => !student.house_id).length;
  const columns = [
    { header: 'House', render: (row) => <HouseName house={row} /> },
    { header: 'Motto', render: (row) => row.motto || '—' },
    { header: 'Students', accessor: 'member_count' },
    {
      header: '',
      render: (row) => (
        <div className={styles.rowActions}>
          <Button variant="secondary" size="sm" onClick={() => setMembers(row)}>
            <UserGroupIcon style={{ width: 14, height: 14 }} />
            Members
          </Button>
          {canManage && (
            <>
              <Button variant="ghost" size="sm" onClick={() => setEditing(row)} aria-label={`Edit ${row.name}`}><PencilIcon style={{ width: 14, height: 14 }} /></Button>
              <Button variant="ghost" size="sm" onClick={() => setRemoving(row)} aria-label={`Remove ${row.name}`}><TrashIcon style={{ width: 14, height: 14, color: 'var(--color-danger)' }} /></Button>
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <div>
      <FilterBar>
        <span className={portal.muted}>
          {unassigned === 0 ? 'Every student is in a house.' : `${unassigned} ${unassigned === 1 ? 'student is' : 'students are'} not in a house yet.`}
        </span>
        {canManage && (
          <Button variant="primary" size="sm" onClick={() => setEditing({})}>
            <PlusIcon style={{ width: 16, height: 16 }} />
            Add house
          </Button>
        )}
      </FilterBar>

      {error && <div className="alert alert-error">{error}</div>}

      <Card>
        <Table columns={columns} data={houses} emptyMessage={canManage ? 'No houses yet. Add the first one to get started.' : 'No houses have been set up yet.'} />
      </Card>

      <HouseModal token={token} house={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); onChanged(); }} />
      <MembersModal token={token} house={members} students={students} onClose={() => setMembers(null)} onSaved={() => { setMembers(null); onChanged(); }} />

      <Modal isOpen={Boolean(removing)} onClose={() => setRemoving(null)} title="Remove house">
        {removing && (
          <div>
            <p className={styles.hint}>
              Remove <strong>{removing.name}</strong>? Its {removing.member_count} {removing.member_count === 1 ? 'student is' : 'students are'} left with no house, and it drops out of the standings. Scores it already earned are kept on record.
            </p>
            <div className={styles.modalActions}>
              <Button variant="danger" onClick={handleRemove}>Remove house</Button>
              <Button variant="secondary" onClick={() => setRemoving(null)}>Keep it</Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

// --------------------------------------------------------------------- Page

function Houses({ token, user }) {
  const canManage = hasAnyRole(user, MANAGE_ROLES);
  const canRecord = hasAnyRole(user, RECORD_ROLES);
  const [tab, setTab] = useState('standings');
  const [termId, setTermId] = useState('');

  const terms = useApi(token, '/api/v1/terms');
  const houses = useApi(token, '/api/v1/houses');
  // Only the roles that record or move students need the whole student list
  const students = useApi(token, canRecord ? '/api/v1/students' : null);

  const tabs = [
    { key: 'standings', label: 'Standings' },
    { key: 'scores', label: 'Scores' },
    canRecord && { key: 'manage', label: canManage ? 'Manage houses' : 'Members' },
  ].filter(Boolean);

  const reloadAll = () => { houses.reload(); students.reload(); };

  return (
    <div>
      <div className={portal.header}>
        <div>
          <h2 className={portal.title}>Houses</h2>
          <p className={portal.subtitle}>House standings come from points given to students for house activities.</p>
        </div>
      </div>

      <div className={portal.tabs} role="tablist">
        {tabs.map((item) => (
          <button key={item.key} type="button" role="tab" aria-selected={tab === item.key} className={tab === item.key ? portal.tabActive : portal.tab} onClick={() => setTab(item.key)}>
            {item.label}
          </button>
        ))}
      </div>

      {tab === 'standings' && <StandingsTab token={token} terms={terms.data || []} termId={termId} setTermId={setTermId} />}
      {tab === 'scores' && (
        <ScoresTab token={token} canRecord={canRecord} terms={terms.data || []} termId={termId} setTermId={setTermId} houses={houses.data || []} students={students.data || []} />
      )}
      {tab === 'manage' && canRecord && (
        <ManageTab token={token} canManage={canManage} houses={houses.data || []} students={students.data || []} onChanged={reloadAll} />
      )}
    </div>
  );
}

export default Houses;
